import { useEffect } from 'react';
import { useFormikContext } from 'formik';
import LocationPicker from 'components/LocationPicker';
import { getZoneFromAddress } from 'constants/operations';
import { useSelector } from 'react-redux';
import { authProfile } from 'redux/auth/selectors';
import { useDebounce } from 'util/Hooks';
import { useMutation } from '@tanstack/react-query';

const useResolveZone = () => {
  const user = useSelector(authProfile);
  return useMutation({
    mutationFn: async ({ address }: { address: string }) => {
      const resolvedZone = await getZoneFromAddress(
        address,
        Number(user?.logisticOperatorID),
      );
      if (!resolvedZone.lat || !resolvedZone.lng) {
        throw new Error(
          'No se pudieron resolver las coordenadas para la dirección proporcionada.',
        );
      }
      return resolvedZone;
    },
  });
};

const GeolocationField = () => {
  const { values, setFieldValue, setFieldError, getFieldMeta } =
    useFormikContext<{
      address: string;
      lat: number | null;
      lng: number | null;
    }>();
  const isTouched = getFieldMeta('address').touched;
  const debouncedAddress = useDebounce(values.address, 500);
  const { mutate: resolveZone } = useResolveZone();

  useEffect(() => {
    if (debouncedAddress && isTouched) {
      resolveZone(
        { address: debouncedAddress }, // Primer parámetro: datos para la mutación
        {
          // Segundo parámetro: opciones de la mutación
          onSuccess: (data) => {
            setFieldValue('address', data.address || debouncedAddress);
            setFieldValue('resolvedZipCode', data.postalCode);
            setFieldValue('resolvedStreet', data.street);
            setFieldValue('resolvedNumber', data.streetNumber);
            setFieldValue('resolvedLocality', data.locality);
            setFieldValue('lat', data.lat);
            setFieldValue('lng', data.lng);
          },
          onError: (error) => {
            setFieldError('address', error.message);
          },
        },
      );
    }
  }, [debouncedAddress, resolveZone, setFieldValue, isTouched]);

  // Función para manejar cambios desde el mapa (NO actualiza la dirección de despacho)
  const handleLocationChange = (locationData: { lat: number; lng: number }) => {
    if (locationData.lat && locationData.lng) {
      setFieldValue('lat', locationData.lat);
      setFieldValue('lng', locationData.lng);
    }
  };

  return (
    <div>
      <LocationPicker
        onLocationChange={handleLocationChange}
        lat={values?.lat}
        lng={values?.lng}
      />
    </div>
  );
};

export default GeolocationField;
